// IOS commands for dynamic routing (Domain 03): router rip / ospf / eigrp / bgp,
// routing-related interface commands, show commands and adjacency syslog.
// Command specs follow the same shape as ./cli.js: { pattern, kinds, run(s, args) }.

import { converge, isLoopback, ifBandwidth, ifDelay, ospfCost, eigrpMetric, routerId } from '../net/routing.js';
import { routingTable } from '../net/sim.js';
import { ipRoute } from './show.js';
import { maskToPrefix, prefixToMask, networkOf, classfulPrefix, inSubnet, isIp, ipToInt } from '../net/addr.js';
import { shortIf, ifSort } from './ifnames.js';

const pad = (s, n) => String(s).padEnd(n);
const lpad = (s, n) => String(s).padStart(n);
const R = (specs) => specs.map((x) => ({ ...x, kinds: ['router'] }));
const uptime = (seed) => { const n = (ipToInt(String(seed)) || String(seed).length * 7919) % 3000; return `00:${String(8 + (n % 50)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`; };
const areaVal = (a) => (isIp(a) ? (a === '0.0.0.0' ? 0 : ipToInt(a)) : +a);

// ------------------------------------------------------------ router sub-modes
export const ROUTER_MODES = ['config-router-rip', 'config-router-ospf', 'config-router-eigrp', 'config-router-bgp'];
export const ROUTER_SUFFIX = Object.fromEntries(ROUTER_MODES.map((m) => [m, '(config-router)#']));

function enter(s, proto, id) {
  const d = s.d;
  if (proto === 'rip') d.rip = d.rip || { version: null, networks: [], autoSummary: true, passive: [], defaultOriginate: false };
  if (proto === 'ospf') { d.ospf = d.ospf || {}; d.ospf[id] = d.ospf[id] || { routerId: null, networks: [], passive: [], passiveDefault: false, notPassive: [], defaultOriginate: false }; }
  if (proto === 'eigrp') { d.eigrp = d.eigrp || {}; d.eigrp[id] = d.eigrp[id] || { networks: [], passive: [], passiveDefault: false, notPassive: [], routerId: null, autoSummary: false }; }
  if (proto === 'bgp') {
    if (d.bgp?.asn && String(d.bgp.asn) !== String(id)) return [`BGP is already running; AS is ${d.bgp.asn}`];
    d.bgp = d.bgp?.asn ? d.bgp : { asn: String(id), routerId: null, neighbors: {}, networks: [] };
  }
  s.state.router = { proto, id: id != null ? String(id) : null };
  s.state.mode = `config-router-${proto}`;
  return [];
}
const cur = (s) => {
  const { proto, id } = s.state.router || {};
  if (proto === 'rip') return s.d.rip;
  if (proto === 'bgp') return s.d.bgp;
  return s.d[proto]?.[id];
};
const classful = (n) => networkOf(n, classfulPrefix(n));
const passive = (p, ifc, on) => {
  if (p.passiveDefault !== undefined && p.passiveDefault) { p.notPassive = (p.notPassive || []).filter((x) => x !== ifc); if (!on) p.notPassive.push(ifc); return []; }
  p.passive = p.passive.filter((x) => x !== ifc);
  if (on) p.passive.push(ifc);
  return [];
};

const COMMON_R = [
  { pattern: 'end', run: (s) => s.endConfig() },
  { pattern: 'do <LINE>', run: (s, [l]) => s.runDo(l) },
  { pattern: 'exit', run: (s) => { s.state.mode = 'config'; s.state.router = null; return []; } },
];
const PASSIVE = [
  { pattern: 'passive-interface <IF>', run: (s, [i]) => passive(cur(s), s.ifn(i), true) },
  { pattern: 'no passive-interface <IF>', run: (s, [i]) => passive(cur(s), s.ifn(i), false) },
];

const RIP = [
  ...COMMON_R, ...PASSIVE,
  { pattern: 'version <1-2>', run: (s, [v]) => { s.d.rip.version = +v; return []; } },
  { pattern: 'no version', run: (s) => { s.d.rip.version = null; return []; } },
  { pattern: 'network <A.B.C.D>', run: (s, [n]) => { const c = classful(n); if (!s.d.rip.networks.includes(c)) s.d.rip.networks.push(c); return []; } },
  { pattern: 'no network <A.B.C.D>', run: (s, [n]) => { s.d.rip.networks = s.d.rip.networks.filter((x) => x !== classful(n)); return []; } },
  { pattern: 'auto-summary', run: (s) => { s.d.rip.autoSummary = true; return []; } },
  { pattern: 'no auto-summary', run: (s) => { s.d.rip.autoSummary = false; return []; } },
  { pattern: 'default-information originate', run: (s) => { s.d.rip.defaultOriginate = true; return []; } },
  { pattern: 'no default-information originate', run: (s) => { s.d.rip.defaultOriginate = false; return []; } },
];

function ospfNetwork(s, n, w, a, add) {
  const p = cur(s);
  const area = areaVal(a);
  p.networks = p.networks.filter((x) => !(x.net === n && x.wild === w));
  if (add) p.networks.push({ net: n, wild: w, area });
  return [];
}
const OSPF = [
  ...COMMON_R, ...PASSIVE,
  { pattern: 'network <A.B.C.D> <A.B.C.D> area <0-4294967295>', run: (s, [n, w, a]) => ospfNetwork(s, n, w, a, true) },
  { pattern: 'network <A.B.C.D> <A.B.C.D> area <A.B.C.D>', run: (s, [n, w, a]) => ospfNetwork(s, n, w, a, true) },
  { pattern: 'no network <A.B.C.D> <A.B.C.D> area <0-4294967295>', run: (s, [n, w, a]) => ospfNetwork(s, n, w, a, false) },
  { pattern: 'no network <A.B.C.D> <A.B.C.D> area <A.B.C.D>', run: (s, [n, w, a]) => ospfNetwork(s, n, w, a, false) },
  { pattern: 'router-id <A.B.C.D>', run: (s, [r]) => { cur(s).routerId = r; return ['% OSPF: Reload or use "clear ip ospf process" command, for this to take effect']; } },
  { pattern: 'no router-id', run: (s) => { cur(s).routerId = null; return []; } },
  { pattern: 'passive-interface default', run: (s) => { const p = cur(s); p.passiveDefault = true; p.notPassive = []; return []; } },
  { pattern: 'no passive-interface default', run: (s) => { const p = cur(s); p.passiveDefault = false; p.passive = []; return []; } },
  { pattern: 'default-information originate', run: (s) => { Object.assign(cur(s), { defaultOriginate: true, always: false }); return []; } },
  { pattern: 'default-information originate always', run: (s) => { Object.assign(cur(s), { defaultOriginate: true, always: true }); return []; } },
  { pattern: 'no default-information originate', run: (s) => { Object.assign(cur(s), { defaultOriginate: false, always: false }); return []; } },
  { pattern: 'auto-cost reference-bandwidth <1-4294967>', run: (s, [b]) => { cur(s).refBw = +b; return ['% OSPF: Reference bandwidth is changed.', '        Please ensure reference bandwidth is consistent across all routers.']; } },
  { pattern: 'log-adjacency-changes', run: () => [] },
];

function eigrpNetwork(s, n, w, add) {
  const p = cur(s);
  const net = w ? n : classful(n);
  p.networks = p.networks.filter((x) => !(x.net === net && (x.wild || null) === (w || null)));
  if (add) p.networks.push({ net, wild: w || null });
  return [];
}
const EIGRP = [
  ...COMMON_R, ...PASSIVE,
  { pattern: 'network <A.B.C.D>', run: (s, [n]) => eigrpNetwork(s, n, null, true) },
  { pattern: 'network <A.B.C.D> <A.B.C.D>', run: (s, [n, w]) => eigrpNetwork(s, n, w, true) },
  { pattern: 'no network <A.B.C.D>', run: (s, [n]) => eigrpNetwork(s, n, null, false) },
  { pattern: 'no network <A.B.C.D> <A.B.C.D>', run: (s, [n, w]) => eigrpNetwork(s, n, w, false) },
  { pattern: 'passive-interface default', run: (s) => { const p = cur(s); p.passiveDefault = true; p.notPassive = []; return []; } },
  { pattern: 'auto-summary', run: (s) => { cur(s).autoSummary = true; return []; } },
  { pattern: 'no auto-summary', run: (s) => { cur(s).autoSummary = false; return []; } },
  { pattern: 'eigrp router-id <A.B.C.D>', run: (s, [r]) => { cur(s).routerId = r; return []; } },
];

const BGP = [
  ...COMMON_R,
  { pattern: 'neighbor <A.B.C.D> remote-as <1-4294967295>', run: (s, [ip, as]) => { const b = s.d.bgp; b.neighbors[ip] = { ...(b.neighbors[ip] || {}), remoteAs: String(as) }; return []; } },
  { pattern: 'no neighbor <A.B.C.D>', run: (s, [ip]) => { delete s.d.bgp.neighbors[ip]; return []; } },
  { pattern: 'no neighbor <A.B.C.D> remote-as <1-4294967295>', run: (s, [ip]) => { delete s.d.bgp.neighbors[ip]; return []; } },
  { pattern: 'neighbor <A.B.C.D> shutdown', run: (s, [ip]) => nb(s, ip, { shutdown: true }) },
  { pattern: 'no neighbor <A.B.C.D> shutdown', run: (s, [ip]) => nb(s, ip, { shutdown: false }) },
  { pattern: 'neighbor <A.B.C.D> default-originate', run: (s, [ip]) => nb(s, ip, { defaultOriginate: true }) },
  { pattern: 'no neighbor <A.B.C.D> default-originate', run: (s, [ip]) => nb(s, ip, { defaultOriginate: false }) },
  { pattern: 'neighbor <A.B.C.D> description <LINE>', run: (s, [ip, t]) => nb(s, ip, { description: t }) },
  { pattern: 'network <A.B.C.D> mask <A.B.C.D>', run: (s, [n, m]) => bgpNet(s, n, m, true) },
  { pattern: 'network <A.B.C.D>', run: (s, [n]) => bgpNet(s, n, prefixToMask(classfulPrefix(n)), true) },
  { pattern: 'no network <A.B.C.D> mask <A.B.C.D>', run: (s, [n, m]) => bgpNet(s, n, m, false) },
  { pattern: 'no network <A.B.C.D>', run: (s, [n]) => bgpNet(s, n, prefixToMask(classfulPrefix(n)), false) },
  { pattern: 'bgp router-id <A.B.C.D>', run: (s, [r]) => { s.d.bgp.routerId = r; return []; } },
  { pattern: 'bgp log-neighbor-changes', run: () => [] },
];
function nb(s, ip, patch) {
  const b = s.d.bgp;
  if (!b.neighbors[ip]) return [`% Specify remote-as or peer-group commands first`];
  Object.assign(b.neighbors[ip], patch);
  return [];
}
function bgpNet(s, n, m, add) {
  const p = maskToPrefix(m);
  if (p === null) return ['% Invalid mask'];
  const b = s.d.bgp;
  b.networks = b.networks.filter((x) => !(x.net === n && x.mask === m));
  if (add) b.networks.push({ net: n, mask: m });
  return [];
}

export const ROUTER_MODE_SPECS = {
  'config-router-rip': R(RIP), 'config-router-ospf': R(OSPF), 'config-router-eigrp': R(EIGRP), 'config-router-bgp': R(BGP),
};

// ------------------------------------------------------------ global config
export const R_ROUTING_CONFIG = R([
  { pattern: 'router rip', run: (s) => enter(s, 'rip', null) },
  { pattern: 'router ospf <1-65535>', run: (s, [p]) => enter(s, 'ospf', +p) },
  { pattern: 'router eigrp <1-65535>', run: (s, [a]) => enter(s, 'eigrp', +a) },
  { pattern: 'router bgp <1-4294967295>', run: (s, [a]) => enter(s, 'bgp', a) },
  { pattern: 'no router rip', run: (s) => { s.d.rip = null; return []; } },
  { pattern: 'no router ospf <1-65535>', run: (s, [p]) => { if (s.d.ospf) delete s.d.ospf[p]; return []; } },
  { pattern: 'no router eigrp <1-65535>', run: (s, [a]) => { if (s.d.eigrp) delete s.d.eigrp[a]; return []; } },
  { pattern: 'no router bgp <1-4294967295>', run: (s, [a]) => { if (String(s.d.bgp?.asn) === String(a)) s.d.bgp = null; return []; } },
  { pattern: 'ip route <A.B.C.D> <A.B.C.D> null0', run: (s, [n, m]) => s.addRoute(n, m, { ifc: 'Null0' }) },
  { pattern: 'ip route <A.B.C.D> <A.B.C.D> null0 <1-255>', run: (s, [n, m, ad]) => s.addRoute(n, m, { ifc: 'Null0', ad: +ad }) },
  { pattern: 'ip route <A.B.C.D> <A.B.C.D> <IF> <1-255>', run: (s, [n, m, i, ad]) => s.addRoute(n, m, { ifc: s.ifn(i), ad: +ad }) },
  { pattern: 'no ip route <A.B.C.D> <A.B.C.D> null0', run: (s, [n, m]) => s.delRoute(n, m, { ifc: 'Null0' }) },
  { pattern: 'no ip route <A.B.C.D> <A.B.C.D> <A.B.C.D> <1-255>', run: (s, [n, m, h]) => s.delRoute(n, m, { nh: h }) },
]);

// ------------------------------------------------------------ interface config
export const R_ROUTING_IF = R([
  { pattern: 'ip ospf cost <1-65535>', run: (s, [c]) => s.eachIf((i) => { i.ospfCost = +c; }) },
  { pattern: 'no ip ospf cost', run: (s) => s.eachIf((i) => { delete i.ospfCost; }) },
  { pattern: 'ip ospf <1-65535> area <0-4294967295>', run: (s, [p, a]) => s.eachIf((i) => { i.ospfArea = { pid: String(p), area: areaVal(a) }; }) },
  { pattern: 'no ip ospf <1-65535> area <0-4294967295>', run: (s) => s.eachIf((i) => { delete i.ospfArea; }) },
  { pattern: 'ip ospf priority <0-255>', run: (s, [p]) => s.eachIf((i) => { i.ospfPriority = +p; }) },
  { pattern: 'ip ospf hello-interval <1-65535>', run: (s, [t]) => s.eachIf((i) => { i.ospfHello = +t; }) },
  { pattern: 'no ip ospf hello-interval', run: (s) => s.eachIf((i) => { delete i.ospfHello; }) },
  { pattern: 'ip ospf mtu-ignore', run: (s) => s.eachIf((i) => { i.ospfMtuIgnore = true; }) },
  { pattern: 'no ip ospf mtu-ignore', run: (s) => s.eachIf((i) => { delete i.ospfMtuIgnore; }) },
  { pattern: 'ip mtu <68-1500>', run: (s, [m]) => s.eachIf((i) => { if (+m === 1500) delete i.ipMtu; else i.ipMtu = +m; }) },
  { pattern: 'no ip mtu', run: (s) => s.eachIf((i) => { delete i.ipMtu; }) },
  { pattern: 'bandwidth <1-10000000>', run: (s, [b]) => s.eachIf((i) => { i.bandwidth = +b; }) },
  { pattern: 'no bandwidth', run: (s) => s.eachIf((i) => { delete i.bandwidth; }) },
  { pattern: 'delay <1-16777215>', run: (s, [d]) => s.eachIf((i) => { i.delay = +d; }) },
  { pattern: 'no delay', run: (s) => s.eachIf((i) => { delete i.delay; }) },
]);

// ------------------------------------------------------------ exec
export const R_ROUTING_PRIV = R([
  { pattern: 'clear ip ospf process', run: () => ({ lines: [], ask: { prompt: 'Reset ALL OSPF processes? [no]: ', answer: (a) => (/^y/i.test(a.trim()) ? [] : []) } }) },
  { pattern: 'clear ip bgp <WORD>', run: (s, [w]) => (w === '*' || isIp(w) ? [] : s.invalidAt(w)) },
  { pattern: 'clear ip route <WORD>', run: (s, [w]) => (w === '*' ? [] : s.invalidAt(w)) },
]);

// ------------------------------------------------------------ show
export const R_ROUTING_SHOW = R([
  { pattern: 'show ip protocols', run: (s) => showProtocols(s.net, s.id) },
  { pattern: 'show ip ospf neighbor', run: (s) => ospfNeighbors(s.net, s.id) },
  { pattern: 'show ip ospf interface brief', run: (s) => ospfIfBrief(s.net, s.id) },
  { pattern: 'show ip ospf interface', run: (s) => ospfInterface(s.net, s.id) },
  { pattern: 'show ip ospf', run: (s) => ospfSummary(s.net, s.id) },
  { pattern: 'show ip ospf database', run: (s) => ospfDatabase(s.net, s.id) },
  { pattern: 'show ip eigrp neighbors', run: (s) => eigrpNeighbors(s.net, s.id) },
  { pattern: 'show ip eigrp topology', run: (s) => eigrpTopology(s.net, s.id) },
  { pattern: 'show ip eigrp interfaces', run: (s) => eigrpInterfaces(s.net, s.id) },
  { pattern: 'show ip bgp summary', run: (s) => bgpSummary(s.net, s.id) },
  { pattern: 'show ip bgp', run: (s) => bgpTable(s.net, s.id) },
  { pattern: 'show ip rip database', run: (s) => ripDatabase(s.net, s.id) },
  ...['ospf', 'eigrp', 'rip', 'bgp', 'static', 'connected'].map((p) => ({ pattern: `show ip route ${p}`, run: (s) => routeFilter(s.net, s.id, p) })),
  { pattern: 'show ip route <A.B.C.D>', run: (s, [a]) => routeDetail(s.net, s.id, a) },
]);

const protoOf = (r) => ({ C: 'connected', L: 'connected', S: 'static', 'S*': 'static' }[r.code] || r.src);
function routeFilter(net, id, p) {
  const keep = (r) => (p === 'connected' ? r.code === 'C' || r.code === 'L' : protoOf(r) === p);
  return ipRoute(net, id, keep);
}
export function routeLine(r, indent = true) {
  const code = pad(r.code, indent ? 9 : 6);
  const dest = `${r.net}/${r.prefix}`;
  if (r.code === 'C' || r.code === 'L') return `${code}${dest} is directly connected, ${r.ifc}`;
  if (r.exitOnly) return `${code}${dest} is directly connected, ${r.ifc}`;
  if (r.src === 'static') return `${code}${dest} [${r.ad}/${r.metric}] via ${r.nh}`;
  return `${code}${dest} [${r.ad}/${r.metric}] via ${r.nh}, ${uptime(r.nh)}, ${r.ifc}`;
}
/** Extra next-hop line for an equal-cost path (IOS indents it under the prefix). */
export const ecmpLine = (r, width) => `${' '.repeat(width)}[${r.ad}/${r.metric}] via ${r.nh}${r.src === 'static' ? '' : `, ${uptime(r.nh)}, ${r.ifc}`}`;

function routeDetail(net, id, ip) {
  const rt = routingTable(net, id).filter((r) => r.code !== 'L' && inSubnet(ip, r.net, r.prefix));
  if (!rt.length) return ['% Network not in table'];
  const top = Math.max(...rt.map((r) => r.prefix));
  const set = rt.filter((r) => r.prefix === top);
  const r = set[0];
  const known = { connected: 'connected', static: 'static', ospf: `ospf ${r.pid || 1}`, eigrp: `eigrp ${r.asn || ''}`.trim(), rip: 'rip', bgp: `bgp ${converge(net).bgp?.state?.[id]?.asn || ''}`.trim() }[protoOf(r)];
  const out = [`Routing entry for ${r.net}/${r.prefix}${r.prefix === 0 ? ', supernet' : ''}`, `  Known via "${known}", distance ${r.ad}, metric ${r.metric}${r.src === 'ospf' ? `, type ${r.code === 'O IA' ? 'inter area' : r.code === 'O*E2' ? 'extern 2, forward metric 1' : 'intra area'}` : ''}${r.code === 'C' ? ' (connected, via interface)' : ''}`];
  if (r.prefix === 0) out.push('  Routing Descriptor Blocks:'); else out.push('  Routing Descriptor Blocks:');
  for (const x of set) {
    if (x.code === 'C') out.push(`  * directly connected, via ${x.ifc}`, '      Route metric is 0, traffic share count is 1');
    else if (x.exitOnly) out.push(`  * directly connected, via ${x.ifc}`, '      Route metric is 0, traffic share count is 1');
    else out.push(`  * ${x.nh}${x.src === 'static' ? '' : `, from ${x.nh}, ${uptime(x.nh)} ago`}, via ${x.ifc}`, `      Route metric is ${x.metric}, traffic share count is 1`);
  }
  return out;
}

// ---- show ip protocols
function sources(net, id, src, ad) {
  const rows = [...new Set(routingTable(net, id).filter((r) => r.src === src).map((r) => r.nh))];
  return ['  Routing Information Sources:', '    Gateway         Distance      Last Update', ...rows.map((g) => `    ${pad(g, 15)} ${lpad(ad, 8)}      ${uptime(g)}`)];
}
function showProtocols(net, id) {
  const d = net.devices[id];
  const res = converge(net);
  const out = ['*** IP Routing is NSF aware ***', ''];
  if (d.rip) {
    const v = d.rip.version;
    const sendTxt = v ? `send version ${v}, receive version ${v}` : 'send version 1, receive any version';
    const ifs = Object.keys(d.interfaces).filter((n) => res.rip.state[id] && d.interfaces[n].ip && d.rip.networks.some((x) => x === networkOf(d.interfaces[n].ip.ip, classfulPrefix(d.interfaces[n].ip.ip))));
    out.push('Routing Protocol is "rip"', '  Outgoing update filter list for all interfaces is not set', '  Incoming update filter list for all interfaces is not set',
      '  Sending updates every 30 seconds, next due in 14 seconds', '  Invalid after 180 seconds, hold down 180, flushed after 240', '  Redistributing: rip',
      `  Default version control: ${sendTxt}`, '    Interface             Send  Recv  Triggered RIP  Key-chain',
      ...ifs.filter((n) => !d.rip.passive.includes(n)).map((n) => `    ${pad(n, 22)}${pad(v || 1, 6)}${v ? v : '1 2'}`),
      `  Automatic network summarization is ${d.rip.autoSummary !== false && v !== 1 ? 'in effect' : d.rip.autoSummary === false ? 'not in effect' : 'in effect'}`,
      '  Maximum path: 4', '  Routing for Networks:', ...d.rip.networks.map((n) => `    ${n}`),
      '  Passive Interface(s):', ...d.rip.passive.map((n) => `    ${n}`), ...sources(net, id, 'rip', 120), '  Distance: (default is 120)', '');
  }
  for (const [pid, p] of Object.entries(d.ospf || {})) {
    const st = res.ospf.state[`${id}|${pid}`];
    const areas = st ? st.areas : [];
    out.push(`Routing Protocol is "ospf ${pid}"`, '  Outgoing update filter list for all interfaces is not set', '  Incoming update filter list for all interfaces is not set',
      `  Router ID ${st?.rid || routerId(net, id, p.routerId) || '0.0.0.0'}`, ...(p.defaultOriginate ? ['  It is an autonomous system boundary router', '  Redistributing External Routes from,'] : []),
      `  Number of areas in this router is ${areas.length}. ${areas.length} normal 0 stub 0 nssa`, '  Maximum path: 4', '  Routing for Networks:',
      ...p.networks.map((n) => `    ${n.net} ${n.wild} area ${n.area}`),
      ...Object.entries(d.interfaces).filter(([, i]) => i.ospfArea && String(i.ospfArea.pid) === String(pid)).map(([n, i]) => `  Routing on Interfaces Configured Explicitly (Area ${i.ospfArea.area}):\n    ${n}`),
      '  Passive Interface(s):', ...(p.passiveDefault ? Object.keys(d.interfaces).filter((n) => !(p.notPassive || []).includes(n)) : p.passive).map((n) => `    ${n}`),
      '  Routing Information Sources:', '    Gateway         Distance      Last Update', ...(st?.neighbors || []).map((n) => `    ${pad(n.rid, 15)} ${lpad(110, 8)}      ${uptime(n.rid)}`),
      '  Distance: (default is 110)', '');
  }
  for (const [asn, p] of Object.entries(d.eigrp || {})) {
    const st = res.eigrp.state[`${id}|${asn}`];
    out.push(`Routing Protocol is "eigrp ${asn}"`, '  Outgoing update filter list for all interfaces is not set', '  Incoming update filter list for all interfaces is not set',
      '  Default networks flagged in outgoing updates', '  Default networks accepted from incoming updates', `  EIGRP-IPv4 Protocol for AS(${asn})`,
      '    Metric weight K1=1, K2=0, K3=1, K4=0, K5=0', '    NSF-aware route hold timer is 240', `    Router-ID: ${st?.rid || routerId(net, id, p.routerId) || '0.0.0.0'}`,
      '    Topology : 0 (base)', '      Active Timer: 3 min', '      Distance: internal 90 external 170', '      Maximum path: 4', '      Maximum hopcount 100', '      Maximum metric variance 1', '',
      `  Automatic Summarization: ${p.autoSummary ? 'enabled' : 'disabled'}`, '  Maximum path: 4', '  Routing for Networks:',
      ...p.networks.map((n) => `    ${n.wild ? `${n.net}/${32 - Math.round(Math.log2(ipToInt(n.wild) + 1))}` : n.net}`),
      '  Passive Interface(s):', ...p.passive.map((n) => `    ${n}`), ...sources(net, id, 'eigrp', 90), '  Distance: internal 90 external 170', '');
  }
  if (d.bgp?.asn) {
    out.push(`Routing Protocol is "bgp ${d.bgp.asn}"`, '  Outgoing update filter list for all interfaces is not set', '  Incoming update filter list for all interfaces is not set',
      '  IGP synchronization is disabled', '  Automatic route summarization is disabled', '  Neighbor(s):', '    Address          FiltIn FiltOut DistIn DistOut Weight RouteMap',
      ...Object.keys(d.bgp.neighbors).map((ip) => `    ${ip}`), '  Maximum path: 1', ...sources(net, id, 'bgp', 20), '  Distance: external 20 internal 200 local 200', '');
  }
  if (out.length === 2) return [];
  return out;
}

// ---- OSPF
function ospfStates(net, id) {
  const res = converge(net);
  return Object.entries(res.ospf.state).filter(([k]) => k.startsWith(`${id}|`)).map(([, v]) => v);
}
function ospfNeighbors(net, id) {
  const out = ['', 'Neighbor ID     Pri   State           Dead Time   Address         Interface'];
  for (const st of ospfStates(net, id)) {
    for (const n of [...st.neighbors, ...(st.pending || [])]) out.push(`${pad(n.rid, 16)}${lpad(n.priority, 3)}   ${pad(`${n.state || 'FULL'}/${n.role === 'DROTHER' ? 'DROTHER' : n.role}`, 16)}00:00:3${ipToInt(n.rid) % 10}    ${pad(n.ip, 16)}${n.ifc}`);
  }
  return out;
}
function ospfIfBrief(net, id) {
  const out = ['Interface    PID   Area            IP Address/Mask    Cost  State Nbrs F/C'];
  for (const st of ospfStates(net, id)) {
    for (const a of [...st.ifs].sort((x, y) => ifSort(x.ifc, y.ifc))) {
      const n = st.neighbors.filter((x) => x.ifc === a.ifc).length;
      const c = n + (st.pending || []).filter((x) => x.ifc === a.ifc).length; // Nbrs F/C: full / counted
      out.push(`${pad(shortIf(a.ifc), 13)}${pad(st.pid, 6)}${pad(a.area, 16)}${pad(`${a.ip}/${a.realPrefix}`, 19)}${pad(a.cost, 6)}${pad(a.role || 'DOWN', 6)}${n}/${c}`);
    }
  }
  return out;
}
function ospfInterface(net, id) {
  const out = [];
  for (const st of ospfStates(net, id)) {
    for (const a of st.ifs) {
      const n = st.neighbors.filter((x) => x.ifc === a.ifc);
      out.push(`${a.ifc} is up, line protocol is up`, `  Internet Address ${a.ip}/${a.realPrefix}, Area ${a.area}, Attached via Network Statement`,
        `  Process ID ${st.pid}, Router ID ${st.rid}, Network Type ${isLoopback(a.ifc) ? 'LOOPBACK' : 'BROADCAST'}, Cost: ${a.cost}`);
      if (isLoopback(a.ifc)) { out.push('  Loopback interface is treated as a stub Host'); continue; }
      if (a.passive) { out.push(`  Transmit Delay is 1 sec, State ${a.role}, Priority ${a.priority}`, '  No Hellos (Passive interface)'); continue; }
      out.push(`  Transmit Delay is 1 sec, State ${a.role}, Priority ${a.priority}`,
        `  Timer intervals configured, Hello ${a.i.ospfHello || 10}, Dead ${(a.i.ospfHello || 10) * 4}, Wait ${(a.i.ospfHello || 10) * 4}, Retransmit 5`,
        `  Neighbor Count is ${n.length}, Adjacent neighbor count is ${n.length}`, ...n.map((x) => `    Adjacent with neighbor ${x.rid}${x.role === 'DR' ? '  (Designated Router)' : x.role === 'BDR' ? '  (Backup Designated Router)' : ''}`));
    }
  }
  return out.length ? out : [];
}
function ospfSummary(net, id) {
  const out = [];
  for (const st of ospfStates(net, id)) {
    out.push(` Routing Process "ospf ${st.pid}" with ID ${st.rid}`, ' Start time: 00:01:12.408, Time elapsed: 00:14:07.112', ' Supports only single TOS(TOS0) routes', ' Supports opaque LSA',
      ' Reference bandwidth unit is 100 mbps', ` Number of areas in this router is ${st.areas.length}. ${st.areas.length} normal 0 stub 0 nssa`,
      ...st.areas.map((a) => `    Area ${a === '0' ? 'BACKBONE(0)' : a}\n        Number of interfaces in this area is ${st.ifs.filter((x) => String(x.area) === a).length}\n        SPF algorithm last executed 00:09:21.004 ago`));
  }
  return out.length ? out : ['%OSPF: Router process not running'];
}
function ospfDatabase(net, id) {
  const sts = ospfStates(net, id);
  if (!sts.length) return [];
  const all = Object.values(converge(net).ospf.state);
  const out = [];
  for (const st of sts) {
    out.push('', `            OSPF Router with ID (${st.rid}) (Process ID ${st.pid})`);
    for (const area of st.areas) {
      out.push('', `                Router Link States (Area ${area})`, '', 'Link ID         ADV Router      Age         Seq#       Checksum Link count');
      for (const o of all.filter((x) => x.areas.includes(area))) out.push(`${pad(o.rid, 16)}${pad(o.rid, 16)}${pad(300 + (ipToInt(o.rid) % 900), 12)}0x80000004 0x00${(ipToInt(o.rid) % 9000).toString(16).padStart(4, '0')} ${o.ifs.filter((x) => String(x.area) === area).length}`);
    }
  }
  return out;
}

// ---- EIGRP
function eigrpStates(net, id) {
  return Object.entries(converge(net).eigrp.state).filter(([k]) => k.startsWith(`${id}|`)).map(([, v]) => v);
}
function eigrpNeighbors(net, id) {
  const out = [];
  for (const st of eigrpStates(net, id)) {
    out.push(`EIGRP-IPv4 Neighbors for AS(${st.asn})`, 'H   Address                 Interface              Hold Uptime   SRTT   RTO  Q  Seq', '                                                   (sec)         (ms)       Cnt Num');
    st.neighbors.forEach((n, k) => out.push(`${pad(k, 4)}${pad(n.ip, 24)}${pad(shortIf(n.ifc), 23)}${lpad(n.holdtime, 4)} ${n.uptime} ${lpad(n.srtt, 4)}  ${lpad(n.rto, 4)}  ${n.q}  ${n.seq}`));
  }
  return out;
}
function eigrpTopology(net, id) {
  const out = [];
  for (const st of eigrpStates(net, id)) {
    out.push(`EIGRP-IPv4 Topology Table for AS(${st.asn})/ID(${st.rid})`, 'Codes: P - Passive, A - Active, U - Update, Q - Query, R - Reply,', '       r - reply Status, s - sia Status ', '');
    const keys = Object.keys(st.topology).sort((a, b) => ipToInt(a.split('/')[0]) - ipToInt(b.split('/')[0]));
    for (const k of keys) {
      const t = st.topology[k];
      out.push(`P ${k}, ${t.successors} successors, FD is ${t.fd}`);
      if (t.own) out.push(`        via Connected, ${t.ifc}`);
      else for (const p of t.paths.filter((x) => x.successor || x.feasible)) out.push(`        via ${p.nh} (${p.metric}/${p.rd}), ${p.ifc}`);
    }
    out.push('');
  }
  return out;
}
function eigrpInterfaces(net, id) {
  const out = [];
  for (const st of eigrpStates(net, id)) {
    out.push(`EIGRP-IPv4 Interfaces for AS(${st.asn})`, '                              Xmit Queue   PeerQ        Mean   Pacing Time   Multicast    Pending', 'Interface              Peers  Un/Reliable  Un/Reliable  SRTT   Un/Reliable   Flow Timer   Routes');
    for (const a of st.ifs.filter((x) => !x.passive)) out.push(`${pad(shortIf(a.ifc), 23)}${lpad(st.neighbors.filter((n) => n.ifc === a.ifc).length, 5)}        0/0       0/0           1       0/0            50           0`);
  }
  return out;
}

// ---- BGP
function bgpState(net, id) { return converge(net).bgp.state[id]; }
function bgpSummary(net, id) {
  const st = bgpState(net, id);
  if (!st) return ['% BGP not active'];
  const entries = Object.keys(st.table).length;
  const out = [`BGP router identifier ${st.rid}, local AS number ${st.asn}`, `BGP table version is ${entries + 1}, main routing table version ${entries + 1}`,
    `${entries} network entries using ${entries * 144} bytes of memory`, `${entries} path entries using ${entries * 80} bytes of memory`, '', 'Neighbor        V           AS MsgRcvd MsgSent   TblVer  InQ OutQ Up/Down  State/PfxRcd'];
  for (const [ip, n] of Object.entries(st.neighbors).sort(([a], [b]) => ipToInt(a) - ipToInt(b))) {
    const est = n.state === 'Established';
    const sent = 12 + (ipToInt(ip) % 9);
    out.push(`${pad(ip, 16)}4 ${lpad(n.remoteAs, 12)} ${lpad(est ? sent + 1 : 0, 7)} ${lpad(est ? sent : 0, 7)} ${lpad(est ? entries + 1 : 1, 8)}    0    0 ${est ? uptime(ip) : 'never   '} ${est ? lpad(n.pfxRcd, 8) : n.state}`);
  }
  return out;
}
function bgpTable(net, id) {
  const st = bgpState(net, id);
  if (!st) return ['% BGP not active'];
  const out = [`BGP table version is ${Object.keys(st.table).length + 1}, local router ID is ${st.rid}`,
    'Status codes: s suppressed, d damped, h history, * valid, > best, i - internal,', '              r RIB-failure, S Stale, m multipath, b backup-path, f RT-Filter,',
    '              x best-external, a additional-path, c RIB-compressed,', 'Origin codes: i - IGP, e - EGP, ? - incomplete', 'RPKI validation codes: V valid, I invalid, N Not found', '',
    '     Network          Next Hop            Metric LocPrf Weight Path'];
  const keys = Object.keys(st.table).sort((a, b) => ipToInt(a.split('/')[0]) - ipToInt(b.split('/')[0]));
  for (const k of keys) {
    const [net0, p] = k.split('/');
    const label = +p === 0 || +p === classfulPrefix(net0) ? net0 : k;
    const paths = Object.values(st.table[k]).sort((a, b) => (b.best ? 1 : 0) - (a.best ? 1 : 0));
    paths.forEach((x, i) => {
      out.push(` ${x.best ? '*>' : '* '}${x.ibgp ? 'i' : ' '}${pad(i ? '' : label, 17)}${pad(x.local ? '0.0.0.0' : x.nh, 20)}${lpad(0, 6)}${lpad(x.ibgp ? 100 : '', 7)}${lpad(x.local ? 32768 : 0, 7)} ${[...x.path, x.origin || 'i'].join(' ')}`);
    });
  }
  return out;
}

// ---- RIP
function ripDatabase(net, id) {
  const rt = routingTable(net, id);
  const st = converge(net).rip.state[id];
  if (!st) return [];
  const out = [];
  for (const a of st.advertised) out.push(`${a}    directly connected`);
  for (const r of rt.filter((x) => x.src === 'rip')) out.push(`${r.net}/${r.prefix}`, `    [${r.metric}] via ${r.nh}, 00:00:1${ipToInt(r.nh) % 9}, ${r.ifc}`);
  return out;
}

// ------------------------------------------------------------ running-config
export function routingConfigLines(d) {
  const out = [];
  if (d.rip) {
    out.push('router rip');
    if (d.rip.version) out.push(` version ${d.rip.version}`);
    for (const n of d.rip.passive) out.push(` passive-interface ${n}`);
    for (const n of d.rip.networks) out.push(` network ${n}`);
    if (d.rip.defaultOriginate) out.push(' default-information originate');
    if (d.rip.autoSummary === false) out.push(' no auto-summary');
    out.push('!');
  }
  for (const [pid, p] of Object.entries(d.ospf || {})) {
    out.push(`router ospf ${pid}`);
    if (p.routerId) out.push(` router-id ${p.routerId}`);
    if (p.refBw) out.push(` auto-cost reference-bandwidth ${p.refBw}`);
    if (p.passiveDefault) { out.push(' passive-interface default'); for (const n of p.notPassive || []) out.push(` no passive-interface ${n}`); } else for (const n of p.passive) out.push(` passive-interface ${n}`);
    for (const n of p.networks) out.push(` network ${n.net} ${n.wild} area ${n.area}`);
    if (p.defaultOriginate) out.push(` default-information originate${p.always ? ' always' : ''}`);
    out.push('!');
  }
  for (const [asn, p] of Object.entries(d.eigrp || {})) {
    out.push(`router eigrp ${asn}`);
    if (p.routerId) out.push(` eigrp router-id ${p.routerId}`);
    if (p.passiveDefault) out.push(' passive-interface default');
    for (const n of p.passive) out.push(` passive-interface ${n}`);
    for (const n of p.networks) out.push(` network ${n.net}${n.wild ? ` ${n.wild}` : ''}`);
    if (p.autoSummary) out.push(' auto-summary');
    out.push('!');
  }
  if (d.bgp?.asn) {
    out.push(`router bgp ${d.bgp.asn}`);
    if (d.bgp.routerId) out.push(` bgp router-id ${d.bgp.routerId}`);
    out.push(' bgp log-neighbor-changes');
    for (const n of d.bgp.networks) out.push(` network ${n.net} mask ${n.mask}`);
    for (const [ip, n] of Object.entries(d.bgp.neighbors)) {
      out.push(` neighbor ${ip} remote-as ${n.remoteAs}`);
      if (n.description) out.push(` neighbor ${ip} description ${n.description}`);
      if (n.shutdown) out.push(` neighbor ${ip} shutdown`);
      if (n.defaultOriginate) out.push(` neighbor ${ip} default-originate`);
    }
    out.push('!');
  }
  return out;
}
export function routingIfLines(n, i) {
  const out = [];
  if (i.bandwidth) out.push(` bandwidth ${i.bandwidth}`);
  if (i.delay) out.push(` delay ${i.delay}`);
  if (i.ospfArea) out.push(` ip ospf ${i.ospfArea.pid} area ${i.ospfArea.area}`);
  if (i.ospfCost) out.push(` ip ospf cost ${i.ospfCost}`);
  if (i.ospfPriority != null) out.push(` ip ospf priority ${i.ospfPriority}`);
  if (i.ospfHello) out.push(` ip ospf hello-interval ${i.ospfHello}`);
  if (i.ipMtu) out.push(` ip mtu ${i.ipMtu}`);
  if (i.ospfMtuIgnore) out.push(' ip ospf mtu-ignore');
  return out;
}

// ------------------------------------------------------------ adjacency syslog
/** Snapshot of this router's adjacencies, for %OSPF / %DUAL / %BGP messages. */
export function adjSnapshot(net, id) {
  const d = net.devices[id];
  if (d.kind !== 'router') return null;
  const res = converge(net);
  const s = { ospf: {}, eigrp: {}, bgp: {}, mism: [] };
  for (const [k, st] of Object.entries(res.ospf.state)) if (k.startsWith(`${id}|`)) for (const n of st.neighbors) s.ospf[`${st.pid}|${n.rid}|${n.ifc}`] = n;
  for (const [k, st] of Object.entries(res.eigrp.state)) if (k.startsWith(`${id}|`)) for (const n of st.neighbors) s.eigrp[`${st.asn}|${n.ip}|${n.ifc}`] = n;
  for (const [ip, n] of Object.entries(res.bgp.state[id]?.neighbors || {})) s.bgp[ip] = n.state;
  s.mism = res.ospf.mismatches.filter((m) => m.dev === id).map((m) => `${m.why}|${m.from}|${m.ifc}|${m.theirs}`);
  return s;
}
export function adjLogs(before, after) {
  if (!before || !after) return [];
  const out = [];
  const L = (text, delay = 500) => out.push({ text, delay, log: true });
  for (const [k, n] of Object.entries(after.ospf)) if (!before.ospf[k]) { const [pid, rid, ifc] = k.split('|'); L(`%OSPF-5-ADJCHG: Process ${pid}, Nbr ${rid} on ${ifc} from LOADING to FULL, Loading Done`, 900); }
  for (const k of Object.keys(before.ospf)) if (!after.ospf[k]) { const [pid, rid, ifc] = k.split('|'); L(`%OSPF-5-ADJCHG: Process ${pid}, Nbr ${rid} on ${ifc} from FULL to DOWN, Neighbor Down: Interface down or detached`); }
  for (const k of Object.keys(after.eigrp)) if (!before.eigrp[k]) { const [as, ip, ifc] = k.split('|'); L(`%DUAL-5-NBRCHANGE: EIGRP-IPv4 ${as}: Neighbor ${ip} (${ifc}) is up: new adjacency`, 700); }
  for (const k of Object.keys(before.eigrp)) if (!after.eigrp[k]) { const [as, ip, ifc] = k.split('|'); L(`%DUAL-5-NBRCHANGE: EIGRP-IPv4 ${as}: Neighbor ${ip} (${ifc}) is down: interface down`); }
  for (const [ip, st] of Object.entries(after.bgp)) {
    if (st === 'Established' && before.bgp[ip] !== 'Established') L(`%BGP-5-ADJCHANGE: neighbor ${ip} Up `, 1200);
    if (st !== 'Established' && before.bgp[ip] === 'Established') L(`%BGP-5-ADJCHANGE: neighbor ${ip} Down User reset`);
  }
  for (const m of after.mism) {
    if (before.mism.includes(m)) continue;
    const [why, from, ifc, theirs] = m.split('|');
    if (why === 'area') L(`%OSPF-4-ERRRCV: Received invalid packet: mismatched area ID${theirs === '0' ? ' from backbone area' : ''} from ${from}, ${ifc}`, 800);
    if (why === 'hello') L(`%OSPF-4-ERRRCV: Received invalid packet: mismatched hello parameters from ${from}, ${ifc}`, 800);
    if (why === 'mtu') L(`%OSPF-5-ADJCHG: Process 1, Nbr ${theirs} on ${ifc} from EXSTART to DOWN, Neighbor Down: Too many retransmissions`, 1200);
  }
  return out;
}

export { ospfCost, ifBandwidth, ifDelay, eigrpMetric };
